import React, { useState, useEffect, useMemo } from "react";
import TransferService from "service/TransferService";
import { useAuth } from "provider/AuthContext";
import SelectDropDown from "components/ui/forms/SelectDropDown";
import SelectedDates from "../FilterFormComponents/DateFilter";
import { TRANSFER_OPTIONS } from "config/DATA";
import { FETCH_DATE_AFTER_SELECTION } from "helper/Functions";
import { ReactComponent as IconSearch } from "assets/icons/icon-search.svg";
import { API_URL } from "helper/Constants";
import CustomTable from "components/ui/tables/CustomTable";
import moment from "moment";
import SectionLoader from "components/ui/loaders/SectionLoader";

const TransferHistory = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("today");
  const [chosenDate, setChosenDate] = useState("");
  const [selectedUser, setSelectedUser] = useState(user?.username || null);
  const [transfers, setTransfers] = useState([]);
  const [showReportData, setShowReportData] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [date, setDate] = useState({ startDate: "", endDate: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  const transferServ = new TransferService(API_URL);

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "gray" : "black",
      borderRadius: "0.375rem",
      "&:hover": {
        borderColor: "gray",
      },
    }),
  };

  const fetchTransferHistory = async () => {
    setLoading(true);
    try {
      let dateOption;

      if (selectedDate === "custom" && chosenDate) {
        const { startDate, endDate } = chosenDate;
        if (!startDate || !endDate) {
          setErrorMessage("Invalid date range selected.");
          return;
        }

        dateOption = { startDate, endDate };
      } else {
        dateOption = selectedDate;
      }

      const username = selectedUser && selectedUser !== "all" ? selectedUser : user.username;

      const result = await transferServ.getTransferHistory(username, date);

      if (result.success) {
        //console.log("Transfer history data:", result.transferHistory);
        let sortedTransfers = result.transferHistory.sort((a, b) => new Date(b.date) - new Date(a.date));
        setTransfers(sortedTransfers);
        setShowReportData(true);
      } else {
        console.error("Error from API:", result.message);
        setErrorMessage(result.message);
        setShowReportData(false);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching transfer history:", error);
      setErrorMessage("An error occurred while fetching transfer history.");
      setShowReportData(false);
      setLoading(false);
    }
  };

  // Removed useEffect for fetchTransferHistory

  const handleSearch = () => {
    fetchTransferHistory();
  };

  const handleSelectTransOptChange = (selectedOption) => {
    setSelectedUser(selectedOption?.value || null);
  };

  // Calculate the difference dates
  useEffect(() => {
    const { startDate, endDate } = FETCH_DATE_AFTER_SELECTION(selectedDate, chosenDate);
    setDate({ ...date, startDate: startDate, endDate: endDate });
  }, [selectedDate, chosenDate]);

  const handleReset = () => {
    setSelectedDate("today");
    setChosenDate("");
    setTransfers([]);
    setSelectedUser(user?.username || null); // Reset to logged-in user
    setShowReportData(false);
    setErrorMessage(null);
  };

  const tableData = transfers?.map((item, index) => {
    return {
      ...item,
    };
  });

  const tableColumns = useMemo(
    () => [
      {
        accessorKey: "senderId.username",
        header: "Sender",
        enableHiding: false,
        size: 100,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.senderId?.username || "N/A"} ({cell.row.original.senderId?.role})
          </>
        ),
      },

      {
        accessorKey: "receiverId.username",
        header: "Receiver",
        enableHiding: false,
        size: 100,
        Cell: ({ cell }) => (
          <>
            {cell.row.original.receiverId?.username || "N/A"} ({cell.row.original.receiverId?.role})
          </>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) => <span className="capitalize">
          {cell.row.original?.transferType === "online"
            ? `Online ${cell.row.original?.type}`
            : cell.row.original?.type}
        </span>
        ,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        enableHiding: false,
        size: 50,
        Cell: ({ cell }) => Math.abs(cell.row.original.amount).toFixed(2),
      },
      {
        accessorKey: "balanceBefore.sender",
        header: "Sender Balance Before",
        size: 179,
        Cell: ({ cell }) => (
          <>{cell.row.original.balanceBefore?.sender != null ? Math.abs(cell.row.original.balanceBefore.sender).toFixed(2) : "N/A"}</>
        ),
      },
      {
        accessorKey: "balanceAfter.sender",
        header: "Sender Balance After",
        size: 179,
        Cell: ({ cell }) => (
          <>{cell.row.original.balanceAfter?.sender != null ? Math.abs(cell.row.original.balanceAfter.sender).toFixed(2) : "N/A"}</>
        ),
      },
      {
        accessorKey: "balanceBefore.receiver",
        header: "Receiver Balance Before",
        size: 179,
        Cell: ({ cell }) => (
          <>{cell.row.original.balanceBefore?.receiver != null ? Math.abs(cell.row.original.balanceBefore.receiver).toFixed(2) : "N/A"}</>
        ),
      },
      {
        accessorKey: "balanceAfter.receiver",
        header: "Receiver Balance After",
        size: 179,
        Cell: ({ cell }) => (
          <>{cell.row.original.balanceAfter?.receiver != null ? Math.abs(cell.row.original.balanceAfter.receiver).toFixed(2) : "N/A"}</>
        ),
      },
      {
        accessorKey: "date",
        header: "Date",
        enableHiding: false,
        size: 100,
        Cell: ({ cell }) => moment?.tz(cell.row.original?.date, "UTC")?.format("YYYY-MM-DD HH:mm:ss"),
      },
    ],
    []
  );

  useEffect(() => {
    // if (user.role !== "Owner") {
    setColumnVisibility({
      "balanceBefore.sender": false,
      "balanceAfter.sender": false,
      "balanceBefore.receiver": false,
      "balanceAfter.receiver": false,
    });
    // }
  }, [setColumnVisibility]);

  return (
    <div className="flex flex-col w-full transfer-history">
      <header className="rounded-lg sm:text-start text-center bg-[#474747] text-white w-full p-3">
        <h1 className="md:text-2xl text-lg font-bold md:text-left text-center">Transfer History</h1>
      </header>
      <div className="flex-1 pt-4">
        <div className="w-full rounded-lg">
          <SelectedDates
            title="Transaction Date"
            setSelectedDate={setSelectedDate}
            selectedDate={selectedDate}
            setCustomDate={setChosenDate}
            setShowReportData={setShowReportData}
          />

          <div className="flex flex-col lg:flex-row space-x-0 space-y-2 lg:space-x-2 lg:space-y-0 w-full">
            <div className="w-[100%] lg:w-[48%] 2xl:w-[38%]">
              <SelectDropDown
                options={TRANSFER_OPTIONS}
                value={TRANSFER_OPTIONS.find((option) => option.value === selectedUser) || null}
                onChange={handleSelectTransOptChange}
                placeholder="Select an option"
                styles={customStyles}
                label="Transfer To / From"
                isSearchable={false}
                className="text-black text-[12px] custom-focus-reset border rounded-md !focus:outline-none !focus:ring-0 !ring-0"
              />
            </div>
          </div>

          <div className="lg:flex gap-3 mb-8 mt-7">
            <button
              className="bg-primary hover:bg-primary/80 p-2 text-black w-full lg:max-w-40 font-bold lg:py-2 px-11 rounded-lg"
              type="button"
              onClick={handleSearch}
            >
              SEARCH
            </button>
            <button
              className="border border-black hover:bg-gray-300 p-2 mt-2 sm:mt-0 text-gray-700 lg:max-w-40 font-bold w-full lg:py-2 px-11 rounded-lg"
              type="reset"
              onClick={handleReset}
            >
              RESET
            </button>
          </div>

          <hr className="border-t mt-5 mb-3 border-[#d4c8c8a3]" />

          {!loading ? (
            showReportData ? (
              <CustomTable
                columns={tableColumns}
                data={tableData ? tableData : []}
                columnVisibility={columnVisibility}
                setColumnVisibility={setColumnVisibility}
                enableGlobalFilter={true}
                enableTopToolbar={true}
                enableHiding={true}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-6 px-3 my-7">
                <div className="bg-[#EDEEF5] rounded-lg p-6 sm:p-8 [&_svg_path]:fill-slate-500">
                  <IconSearch />
                </div>
                <span className="text-lg sm:text-xl mt-8 mb-4 text-[#474747] text-center w-full max-w-[300px]">
                  Search all available records by selecting one of the filters above!
                </span>
              </div>
            )
          ) : (
            <div className="flex justify-center py-8">
              <SectionLoader bgColor="light" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferHistory;
